// «Где остановился»: при повторном открытии отчёта курсор и прокрутка возвращаются туда, где работали в прошлый раз
// (localStorage этого браузера; без него просто откроется начало документа).
import type { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import type { RegistryApi } from '../../core/registry';
import { appStore, readPref, writePref } from '../../core/store';

interface Pos { from: number; scroll: number; t: number }
const key = (pid: string) => 'pos.' + pid;

function save(pid: string, ed: Editor) {
  const c = document.querySelector('.canvas') as HTMLElement | null;
  const p: Pos = { from: ed.state.selection.from, scroll: c?.scrollTop || 0, t: Date.now() };
  writePref(key(pid), JSON.stringify(p));
}

export function setupPosition(r: RegistryApi) {
  let timer = 0;
  let cleanup: (() => void) | null = null;
  let last: { pid: string; ed: Editor } | null = null;
  r.events.on('editor:created', (ed: Editor) => {
    cleanup?.();
    const pid = appStore.getState().projectId;
    if (!pid) return;
    last = { pid, ed };
    let saved: Pos | null = null;
    try { saved = JSON.parse(readPref(key(pid), '') || 'null'); } catch { saved = null; }
    // в автотестах (navigator.webdriver) не двигаем курсор и прокрутку сами - чтобы не сбивать щелчки тестов
    const auto = !(navigator as any).webdriver || readPref('position.force', '') === '1';
    if (!auto) { /* только запоминаем */ } else if (saved && saved.from > 1 && saved.from < ed.state.doc.content.size) {
      const target = saved;
      // после первой вёрстки (картинки, нумерация) - вернуть курсор и прокрутку
      setTimeout(() => {
        if (ed.isDestroyed) return;
        try { ed.view.dispatch(ed.state.tr.setSelection(TextSelection.near(ed.state.doc.resolve(target.from)))); } catch { /* документ изменился */ }
        const c = document.querySelector('.canvas') as HTMLElement | null;
        if (c) c.scrollTop = target.scroll;
      }, 150);
    } else {
      // первое открытие: сразу к тексту (титул и служебные листы - выше, их видно прокруткой), курсор в начало
      requestAnimationFrame(() => {
        if (ed.isDestroyed) return;
        const c = document.querySelector('.canvas') as HTMLElement | null;
        const main = document.querySelector('.sheet-main') as HTMLElement | null;
        if (c && main) c.scrollTop = Math.max(0, main.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop - 16);
        if (document.activeElement === document.body) ed.commands.focus('start', { scrollIntoView: false });
      });
    }
    const later = () => { window.clearTimeout(timer); timer = window.setTimeout(() => { if (!ed.isDestroyed) save(pid, ed); }, 800); };
    ed.on('selectionUpdate', later);
    const canvas = document.querySelector('.canvas');
    canvas?.addEventListener('scroll', later, { passive: true });
    cleanup = () => { ed.off('selectionUpdate', later); canvas?.removeEventListener('scroll', later); };
  });
  r.events.on('project:closed', () => {
    window.clearTimeout(timer);
    if (last) { try { save(last.pid, last.ed); } catch { /* редактор уже закрыт */ } }
    cleanup?.(); cleanup = null; last = null;
  });
}
